<script setup lang="ts">
    import { ref, watch, onBeforeUnmount } from 'vue'
    import {
        useEditor,
        EditorContent,
        type Editor as TiptapEditor,
    } from '@tiptap/vue-3'
    import StarterKit from '@tiptap/starter-kit'

    enum OutputFormats {
        html = 'html',
        json = 'json',
    }

    const props = withDefaults(
        defineProps<{
            modelValue?: string
            label?: string
            outputFormat?: OutputFormats
            readonly?: boolean
        }>(),
        {
            modelValue: '',
            label: '',
            outputFormat: OutputFormats.html,
            readonly: false,
        },
    )
    const emits = defineEmits(['update:modelValue'])

    const fontSize = ref(10)

    const editor = useEditor({
        extensions: [StarterKit],
        editable: !props.readonly,
        content: props.modelValue,
        onUpdate: () => {
            if (props.outputFormat === OutputFormats.json) {
                emits('update:modelValue', typedEditor.value?.getJSON())
            } else {
                emits('update:modelValue', typedEditor.value?.getHTML())
            }
        },
    })
    // Typed accessor for editor methods (workaround for Volar/TS6 class inheritance resolution with @tiptap/vue-3)
    const typedEditor = editor as { readonly value: TiptapEditor | undefined }

    watch(
        () => props.modelValue,
        (newValue) => {
            if (props.outputFormat === OutputFormats.json) {
                if (
                    JSON.stringify(typedEditor.value?.getJSON()) ===
                    JSON.stringify(newValue)
                ) {
                    return
                }
            } else {
                if (typedEditor.value?.getHTML() === newValue) {
                    return
                }
            }
            typedEditor.value?.commands.setContent(newValue)
        },
    )

    const buttons = [
        {
            icon: 'octicon:bold-16',
            title: 'Bold',
            action: () => typedEditor.value?.chain().focus().toggleBold().run(),
            isActive: () => typedEditor.value?.isActive('bold'),
        },
        {
            icon: 'italic',
            title: 'Italic',
            action: () =>
                typedEditor.value?.chain().focus().toggleItalic().run(),
            isActive: () => typedEditor.value?.isActive('italic'),
        },
        {
            icon: 'lucide:heading-1',
            title: 'Heading 1',
            action: () =>
                typedEditor.value
                    ?.chain()
                    .focus()
                    .toggleHeading({ level: 1 })
                    .run(),
            isActive: () =>
                typedEditor.value?.isActive('heading', { level: 1 }),
        },
        {
            icon: 'lucide:heading-2',
            title: 'Heading 2',
            action: () =>
                typedEditor.value
                    ?.chain()
                    .focus()
                    .toggleHeading({ level: 2 })
                    .run(),
            isActive: () =>
                typedEditor.value?.isActive('heading', { level: 2 }),
        },
        {
            icon: 'ri:paragraph',
            title: 'Paragraph',
            action: () =>
                typedEditor.value?.chain().focus().setParagraph().run(),
            isActive: () => typedEditor.value?.isActive('paragraph'),
        },
        {
            icon: 'ri:list-unordered',
            title: 'Bullet List',
            action: () =>
                typedEditor.value?.chain().focus().toggleBulletList().run(),
            isActive: () => typedEditor.value?.isActive('bulletList'),
        },
        {
            icon: 'ri:list-ordered',
            title: 'Ordered List',
            action: () =>
                typedEditor.value?.chain().focus().toggleOrderedList().run(),
            isActive: () => typedEditor.value?.isActive('orderedList'),
        },
        {
            icon: 'clear-style',
            title: 'Clear Format',
            action: () =>
                typedEditor.value
                    ?.chain()
                    .focus()
                    .clearNodes()
                    .unsetAllMarks()
                    .run(),
        },
    ]

    const selectedButtons = ref([])
    onBeforeUnmount(() => typedEditor.value?.destroy())
</script>

<template>
    <div class="vv-input-text wysiwyg">
        <label v-if="label" :for="label" class="vv-input-text__label">
            {{ label }}
        </label>
        <div class="vv-input-text__wrapper flex-col items-start">
            <EditorContent
                :id="label"
                class="wysiwyg__content light-scrollbar preflight"
                :editor="editor" />
            <VvButtonGroup
                v-if="!readonly"
                v-model="selectedButtons"
                modifiers="compact"
                item-modifiers="action-quiet"
                class="wysiwyg__actions"
                multiple>
                <VvButton
                    v-for="(button, index) in buttons"
                    :key="`${button.title}_${index}`"
                    :title="button.title"
                    :icon="button.icon"
                    :pressed="button.isActive?.()"
                    @click="button.action" />
                <div>
                    <VvInputText
                        v-model="fontSize"
                        min="5"
                        name="font-size"
                        type="number"
                        class="wysiwyg__font-size" />
                </div>
            </VvButtonGroup>
        </div>
    </div>
</template>

<style lang="scss">
    .tiptap {
        flex-grow: 1;

        h1,
        h2,
        h3,
        p {
            margin: 0;
            vertical-align: middle;
        }
    }
</style>
